import { Link } from "@/components/LocalLink";
import { JARS, DOGS, DOG_PICKS, productById, productPath, formatShort } from "@/data/catalog";
import { BuyButton } from "@/components/BuyButton";
import { useLang } from "@/i18n/LanguageContext";

export const dogName = (d) => d.display || d.name;

export const TrustStrip = () => (
  <div className="trust" data-testid="trust-strip"><div className="wrap">
    <div><b>Every ingredient named</b></div>
    <div><b>Ships across Canada</b> · free over $125</div>
    <div><b>One measure a day</b>, by weight</div>
    <div><b>Unopened jars</b> returnable 30 days</div>
  </div></div>
);

const Card = ({ p }) => (
  <div className="card" data-testid={`product-card-${p.id}`}>
    <Link to={productPath(p)} className="ph" data-testid={`product-card-link-${p.id}`} style={{ display: "block" }}><img src={p.image} alt={`${p.name} jar`} loading="lazy" /></Link>
    <div className="body">
      <span className="best" data-testid={`product-card-pill-${p.id}`}>{p.bestFor}</span>
      <Link to={productPath(p)}><h3>{p.name}</h3></Link>
      <div className="what">{p.plain}</div>
      <div className="row"><span className="price" data-testid={`product-card-price-${p.id}`}>{formatShort(p.priceCents)}</span><span className="size">60 g · 60 measures</span></div>
      <BuyButton productIds={p.id} className="btn btn-primary" testid={`product-card-buy-${p.id}`} />
    </div>
  </div>
);

export const ShopGrid = ({ as: H = "h2" }) => {
  const dram = productById("discovery_dram");
  return (
    <section className={`shop${H === "h1" ? " shop-page" : ""}`} id="shop" data-testid="shop-grid"><div className="wrap">
      <div className="head">
        <div><div className="eyebrow">The Pawthecary</div><H>Shop the jars</H></div>
        <p>Four preparations, one for each kind of dog. Not sure? Take the five-question quiz or start with the Discovery Dram.</p>
      </div>
      <div className="grid">{JARS.map((p) => <Card key={p.id} p={p} />)}</div>
      <div className="start">
        <div className="tile" data-testid="tile-discovery-dram">
          <Link to={productPath(dram)}><img src={dram.image} alt="The Discovery Dram, three small drams" /></Link>
          <div><div className="eyebrow">Start here</div><Link to={productPath(dram)}><h4>The Discovery Dram · $29</h4></Link>
            <p>Vigor, Calm and the Guard in three small drams. Two weeks to let your dog cast the deciding vote.</p>
            <BuyButton productIds="discovery_dram" className="btn-text" testid="tile-dram-buy" /></div>
        </div>
        <div className="tile quiz" data-testid="tile-find-your-jar">
          <div className="big" aria-hidden="true">?</div>
          <div><div className="eyebrow">Not sure which?</div><h4>Find your dog's jar</h4>
            <p>Five quick questions. One recommendation, with the measure for their weight.</p>
            <Link to="/find-your-jar" className="btn-text" data-testid="tile-take-quiz">Take the quiz</Link></div>
        </div>
      </div>
    </div></section>
  );
};

const WHY = [
  ["01", "The whole story", "Every ingredient named, every source stated. If we can't say where it grew, it doesn't go in the jar."],
  ["02", "Small runs", "Batches small enough to know personally. Your jar carries its number."],
  ["03", "No theatre", "No miracle claims and no borrowed science. Mushrooms, honestly prepared."],
  ["04", "The dogs decide", "Every preparation passes the only panel that matters. Uncompromising, and usually asleep until noon."],
];

export const StandardStrip = () => (
  <section className="why" data-testid="standard-strip"><div className="wrap">
    <div className="eyebrow">The Standard</div>
    <div className="four">{WHY.map(([n, h, p]) => <div key={n}><div className="n">{n}</div><h4>{h}</h4><p>{p}</p></div>)}</div>
  </div></section>
);

export const PickLink = ({ dog, testid, arrow = false, className }) => {
  const { t } = useLang();
  const pick = DOG_PICKS[dog.name];
  const p = productById(pick.productId);
  const price = formatShort(p.priceCents);
  const lead = t(`${pick.pronoun} pick:`, pick.pronoun === "Her" ? "Son choix à elle :" : "Son choix à lui :");
  return (
    <Link to={productPath(p)} data-testid={testid} className={className} data-no-translate>
      {lead} {arrow ? <b>{p.name}</b> : p.name.replace(/^The /, "")} <span>{arrow ? `${price} →` : `· ${price}`}</span>
    </Link>
  );
};

const QUOTES = { Ezekiel: "Does not explain his choices.", Kane: "Hears the storm an hour before you do.", Malakai: "Works weekends. Does not understand weekends.", Ele: "Has never once been wrong." };

export const StaffPicks = () => (
  <section className="pack" data-testid="staff-picks"><div className="wrap">
    <div className="head"><div><div className="eyebrow">Founded by four good dogs</div><h2>Meet the staff, and their picks</h2></div>
      <Link to="/our-story" className="btn-text" data-testid="staff-read-story">Read our story</Link></div>
    <div className="dogs">
      {DOGS.map((d) => (
        <div className="dog" key={d.name} data-testid={`staff-${d.slug}`}>
          <Link to={`/founders/${d.slug}`}><h4>{dogName(d)}</h4></Link>
          <div className="role">{d.title}</div>
          <p>“{QUOTES[d.name]}”</p>
          <PickLink dog={d} testid={`staff-pick-${d.slug}`} />
        </div>
      ))}
    </div>
  </div></section>
);

const QA = [
  ["Which jar is right for my dog?", "The Forester's Guard suits most dogs, most days. The quiz narrows it down in five questions."],
  ["How much do I give?", "By weight: a quarter measure to 25 lb, half to 50 lb, three quarters to 90 lb, one full measure above. Sprinkled on food or mixed in."],
  ["How long does a jar last?", "60 measures: about 240 days for a small dog, 120 for a medium dog, 60 for the largest."],
  ["Is it safe with my dog's medication?", "Ask your veterinarian before starting anything new, especially if your dog is unwell or on medication."],
];

export const GoodQuestions = () => (
  <section className="faq" data-testid="good-questions"><div className="wrap">
    <div className="head"><div><div className="eyebrow">Before you buy</div><h2>Good questions</h2></div>
      <Link to="/help#questions" className="btn-text" data-testid="good-questions-help">All questions</Link></div>
    <div className="qs">{QA.map(([q, a]) => <div className="q" key={q}><b>{q}</b><span>{a}</span></div>)}</div>
  </div></section>
);
